import React, { useEffect, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { ARRAY_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, intlShape, injectIntl } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT, propTypes } from '../../../../util/types';
import { nonEmptyArray, composeValidators } from '../../../../util/validators';
import { isUploadImageOverLimitError } from '../../../../util/errors';

// Import shared components
import {
  Button,
  Form,
  AspectRatioWrapper,
  FieldTextInput,
  FieldRadioButton,
} from '../../../../components';

// Import modules from this directory
import ListingImage from './ListingImage';
import css from './EditListingPhotosForm.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { setImageDesc } from '../../EditListingPage.duck';

const ACCEPT_IMAGES = 'image/*';
const IMAGE_DISCRIPTION_MAX_LENGTH = 70;

const ImageUploadError = props => {
  return props.uploadOverLimit ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadOverLimit" />
    </p>
  ) : props.uploadImageError ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
    </p>
  ) : null;
};

// NOTE: PublishListingError and ShowListingsError are here since Photos panel is the last visible panel
// before creating a new listing. If that order is changed, these should be changed too.
// Create and show listing errors are shown above submit button
const PublishListingError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.publishListingFailed" />
    </p>
  ) : null;
};

const ShowListingsError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.showListingFailed" />
    </p>
  ) : null;
};

// Field component that uses file-input to allow user to select images.
export const FieldAddImage = props => {
  const { formApi, onImageUploadHandler, aspectWidth = 1, aspectHeight = 1, ...rest } = props;
  return (
    <Field form={null} {...rest}>
      {fieldprops => {
        const { accept, input, label, disabled: fieldDisabled } = fieldprops;
        const { name, type } = input;
        const onChange = e => {
          const file = e.target.files[0];
          formApi.change(`addImage`, file);
          formApi.blur(`addImage`);
          onImageUploadHandler(file);
        };
        const inputProps = { accept, id: name, name, onChange, type };
        return (
          <div className={css.addImageWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              {fieldDisabled ? null : <input {...inputProps} className={css.addImageInput} />}
              <label htmlFor={name} className={css.addImage}>
                {label}
              </label>
            </AspectRatioWrapper>
          </div>
        );
      }}
    </Field>
  );
};

// Component that shows listing images from "images" field array
const FieldListingImage = props => {
  const { name, intl, onRemoveImage, aspectWidth, aspectHeight, variantPrefix } = props;
  return (
    <Field name={name}>
      {fieldProps => {
        const { input } = fieldProps;
        const image = input.value;
        return image ? (
          <ListingImage
            image={image}
            key={image?.id?.uuid || image?.id}
            className={css.thumbnail}
            savedImageAltText={intl.formatMessage({
              id: 'EditListingPhotosForm.savedImageAltText',
            })}
            onRemoveImage={() => onRemoveImage(image?.id)}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
          />
        ) : null;
      }}
    </Field>
  );
};

export const EditListingPhotosFormComponent = props => {
  const [state, setState] = useState({ imageUploadRequested: false });
  const [submittedImages, setSubmittedImages] = useState([]);
  const onImageUploadHandler = file => {
    const { listingImageConfig, onImageUpload } = props;
    if (file) {
      setState({ imageUploadRequested: true });
      onImageUpload({ id: `${file.name}_${Date.now()}`, file }, listingImageConfig)
        .then(() => {
          setState({ imageUploadRequested: false });
        })
        .catch(() => {
          setState({ imageUploadRequested: false });
        });
    }
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize
      render={formRenderProps => {
        const {
          form,
          formId,
          className,
          fetchErrors,
          handleSubmit,
          intl,
          invalid,
          onRemoveImage,
          disabled,
          ready,
          saveActionMsg,
          draftButtonText,
          saveAsDraftInProgress,
          updated,
          updateInProgress,
          touched,
          errors,
          values,
          listingImageConfig,
        } = formRenderProps;

        const { images, primaryImageId, state: listingState } = values;
        const { aspectWidth = 1, aspectHeight = 1, variantPrefix } = listingImageConfig;

        const { publishListingError, showListingsError, updateListingError, uploadImageError } =
          fetchErrors || {};
        const uploadOverLimit = isUploadImageOverLimitError(uploadImageError);

        // imgs can contain added images (with temp ids) and submitted images with uniq ids.
        const arrayOfImgIds = imgs => imgs.map(i => (typeof i.id === 'string' ? i.imageId : i.id));
        const imageIdsFromProps = arrayOfImgIds(images);
        const imageIdsFromPreviousSubmit = arrayOfImgIds(submittedImages);
        const imageArrayHasSameImages = isEqual(imageIdsFromProps, imageIdsFromPreviousSubmit);
        const submittedOnce = submittedImages.length > 0;
        const pristineSinceLastSubmit = submittedOnce && imageArrayHasSameImages;

        const submitReady = (updated && pristineSinceLastSubmit) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid ||
          disabled ||
          submitInProgress ||
          state.imageUploadRequested ||
          ready ||
          images.length < 3;
          
        const submitDraftDisabled =
          (invalid && images.length > 0) ||
          disabled ||
          submitInProgress ||
          state.imageUploadRequested ||
          ready;
        const imagesError = touched.images && errors?.images && errors.images[ARRAY_ERROR];
        const classes = classNames(css.root, className);

        useEffect(() => {
          if (values.images?.length === 1)
            form.change('primaryImageId', values.images[0]?.imageId?.uuid);
        }, [values.images]);

        return (
          <Form
            className={classes}
            onSubmit={e => {
              setSubmittedImages(images);
              handleSubmit(e);
            }}
          >
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingPhotosForm.updateFailed" />
              </p>
            ) : null}

            <div className={css.imagesFieldArray}>
              <FieldArray
                name="images"
                validate={composeValidators(
                  nonEmptyArray(
                    intl.formatMessage({
                      id: 'EditListingPhotosForm.imageRequired',
                    })
                  )
                )}
              >
                {({ fields }) =>
                  fields.map((name, index) => {
                    const currentImageId =
                      fields.value[index]?.imageId?.uuid ?? fields.value[index]?.id?.uuid;
                    const isPrimaryImage = currentImageId === primaryImageId;
                    return (
                      <div key={name} className={css.imageContainerWrapper}>
                        <div className={css.imageContainer}>
                          <FieldListingImage
                            key={name}
                            name={name}
                            onRemoveImage={imageId => {
                              fields.remove(index);
                              onRemoveImage(imageId);
                            }}
                            intl={intl}
                            aspectWidth={aspectWidth}
                            aspectHeight={aspectHeight}
                            variantPrefix={variantPrefix}
                          />

                          <div className={css.radioButtonContainer}>
                            <FieldRadioButton
                              className={css.radioButton}
                              id={`primaryImage_${index}_option`}
                              name="primaryImageId"
                              label="Primary Image"
                              value={`${index}`}
                              key={`primaryImage_${index}_key`}
                              checked={isPrimaryImage}
                              onChange={() => {
                                form.change('primaryImageId', currentImageId);
                              }}
                              isCheckBoxStyle={true}
                            />
                          </div>
                        </div>

                        <div className={css.textareaInputWrapper}>
                          {!!currentImageId && (
                            <>
                              <FieldTextInput
                                id={`${formId}imageDescription`}
                                name={currentImageId}
                                className={css.title}
                                type="textarea"
                                placeholder={intl.formatMessage({
                                  id: 'EditListingPhotosForm.imageDescriptionPlaceholder',
                                })}
                                maxLength={IMAGE_DISCRIPTION_MAX_LENGTH}
                              />

                              {/* charactor count */}
                              <p className={css.destinationCharCount}>
                                {values?.[`${currentImageId}`]?.length || 0}/
                                {IMAGE_DISCRIPTION_MAX_LENGTH}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </FieldArray>

              {images.length < 8 && (
                <FieldAddImage
                  id="addImage"
                  name="addImage"
                  accept={ACCEPT_IMAGES}
                  label={
                    <span className={css.chooseImageText}>
                      <span>
                        <svg
                          width="70"
                          height="71"
                          viewBox="0 0 70 71"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.9183 38.4583H17.5C15.1794 38.4583 12.9538 37.5233 11.3128 35.8589C9.67187 34.1945 8.75 31.9371 8.75 29.5833C8.75 27.2295 9.67187 24.9722 11.3128 23.3078C12.9538 21.6434 15.1794 20.7083 17.5 20.7083C18.3167 20.7083 18.725 20.7083 19.0079 20.6492C19.8771 20.4717 20.2913 20.2025 20.8046 19.4688C20.9738 19.2321 21.2129 18.6937 21.6883 17.6169C22.8388 15.0119 24.7077 12.8001 27.0697 11.2482C29.4316 9.69625 32.1858 8.87051 35 8.87051C37.8142 8.87051 40.5684 9.69625 42.9303 11.2482C45.2923 12.8001 47.1612 15.0119 48.3117 17.6169C48.7871 18.6937 49.0263 19.2292 49.1954 19.4688C49.7117 20.2025 50.1229 20.4746 50.9921 20.6521C51.2779 20.7083 51.6863 20.7083 52.5 20.7083C54.8206 20.7083 57.0462 21.6434 58.6872 23.3078C60.3281 24.9722 61.25 27.2295 61.25 29.5833C61.25 31.9371 60.3281 34.1945 58.6872 35.8589C57.0462 37.5233 54.8206 38.4583 52.5 38.4583H49.0817L39.1242 28.3586L35 24.1755L30.8758 28.3586L20.9183 38.4583Z"
                            fill="#4B5563"
                          />
                          <path
                            d="M35.0002 35.5L32.9382 33.4085L35.0002 31.3169L37.0623 33.4085L35.0002 35.5ZM37.9169 62.125C37.9169 62.9096 37.6096 63.6621 37.0626 64.2169C36.5156 64.7717 35.7738 65.0833 35.0002 65.0833C34.2267 65.0833 33.4848 64.7717 32.9378 64.2169C32.3909 63.6621 32.0836 62.9096 32.0836 62.125H37.9169ZM21.2715 45.2418L32.9382 33.4085L37.0623 37.5915L25.3956 49.4249L21.2715 45.2418ZM37.0623 33.4085L48.729 45.2418L44.6048 49.4249L32.9382 37.5915L37.0623 33.4085ZM37.9169 35.5V62.125H32.0836V35.5H37.9169Z"
                            fill="#4B5563"
                          />
                        </svg>
                      </span>
                      <span className={css.chooseImage}>
                        <FormattedMessage id="EditListingPhotosForm.chooseImage" />
                      </span>
                      <span className={css.imageTypes}>
                        <FormattedMessage id="EditListingPhotosForm.imageTypes" />
                      </span>
                    </span>
                  }
                  type="file"
                  disabled={state.imageUploadRequested}
                  formApi={form}
                  onImageUploadHandler={onImageUploadHandler}
                  aspectWidth={aspectWidth}
                  aspectHeight={aspectHeight}
                />
              )}
            </div>

            {imagesError ? <div className={css.arrayError}>{imagesError}</div> : null}

            <ImageUploadError
              uploadOverLimit={uploadOverLimit}
              uploadImageError={uploadImageError}
            />

            {images.length < 3 && (
              <p className={css.tip}>
                <FormattedMessage id="EditListingPhotosForm.minimumImages" />
              </p>
            )}

            <PublishListingError error={publishListingError} />
            <ShowListingsError error={showListingsError} />

            <div className={css.submitButtonContainer}>
              <Button
                className={css.submitButton}
                type="submit"
                onClick={() => {
                  form.batch(() => {
                    form.change('saveDraftOnly', false);
                    form.change('draftButtonClicked', false);
                  });
                }}
                inProgress={
                  submitInProgress && !form.getState().values?.draftButtonClicked && !saveAsDraftInProgress
                }
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </Button>
              {listingState === LISTING_STATE_DRAFT && (
                <Button
                  className={css.draftButton}
                  type="submit"
                  onClick={() => {
                    form.batch(() => {
                      form.change('saveDraftOnly', true);
                      form.change('draftButtonClicked', true);
                      form.pauseValidation();
                    });
                  }}
                  inProgress={saveAsDraftInProgress && !submitInProgress}
                  disabled={submitDraftDisabled}
                  ready={submitReady && values?.draftButtonClicked}
                >
                  {draftButtonText}
                </Button>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

EditListingPhotosFormComponent.defaultProps = { fetchErrors: null };

EditListingPhotosFormComponent.propTypes = {
  fetchErrors: shape({
    publishListingError: propTypes.error,
    showListingsError: propTypes.error,
    uploadImageError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  intl: intlShape.isRequired,
  onImageUpload: func.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  listingImageConfig: object.isRequired,
};

export default compose(injectIntl)(EditListingPhotosFormComponent);
